/**
 * GeoMap — reusable OSM map (Leaflet).
 * Always paints a real base map (default CZ), even with zero markers.
 * Used by geo.map widget and /modules/geo page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Building2, Eye, EyeOff, Link2, MapPin, RefreshCw, Server, Zap } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MapSnapshot {
  ok: boolean;
  default_center?: { lat: number; lon: number; zoom: number };
  summary?: {
    positions: number;
    by_kind?: Record<string, number>;
    callers?: number;
    callers_with_coords?: number;
    callers_mapped?: number;
    network_markers?: number;
    customers?: number;
    nodes_missing_position?: number;
    nodes_approximate?: number;
    sessions?: number;
    keys?: number;
    session_window?: string;
    attacks?: number;
    attack_links?: number;
    inventory_nodes?: number;
  };
  positions: Array<{
    id: string;
    kind: string;
    entity_id: string;
    label: string | null;
    lat: number;
    lon: number;
    source: string;
    missing_precise_position?: boolean;
    position_quality?: string;
    metadata?: Record<string, unknown>;
  }>;
  callers: Array<{
    id: string;
    ip: string;
    lat: number | null;
    lon: number | null;
    is_private: boolean;
    city?: string | null;
    country_code?: string | null;
    customer_id?: string | null;
    customer_label?: string | null;
    color?: string | null;
    assigned_label?: string | null;
  }>;
  network_markers?: Array<{
    id: string;
    cidr: string;
    label: string;
    customer_id: string | null;
    customer_label: string | null;
    kind: string;
    is_private: boolean;
    color: string | null;
    lat: number;
    lon: number;
  }>;
  sessions?: Array<{
    session_id: string;
    entity_id: string;
    username: string | null;
    node_id: string;
    auth_type: string | null;
    ip_address: string | null;
    last_active: string | null;
    operator_lat: number | null;
    operator_lon: number | null;
    node_lat: number | null;
    node_lon: number | null;
    lat: number | null;
    lon: number | null;
    geo_source?: string | null;
  }>;
  keys?: Array<{
    id: string;
    entity_id: string | null;
    username: string | null;
    label: string;
    key_preview: string;
    last_used: string | null;
    use_count: number;
    lat: number | null;
    lon: number | null;
    ip_address: string | null;
    geo_source: string | null;
  }>;
  session_window?: string;
  activity_acl?: {
    mode?: string;
    scope?: string;
    summary?: {
      source_nodes?: string[];
      sessions_cluster?: number;
      keys_cluster?: number;
    };
  };
  attacks?: Array<{
    ip: string;
    lat: number;
    lon: number;
    event_count: number;
    kinds: string[];
    severity_max: string | null;
    target_nodes: string[];
    blacklisted: boolean;
    country_code: string | null;
    city: string | null;
    links: Array<{ node_id: string; node_lat: number; node_lon: number }>;
  }>;
  customers?: Array<{ id: string; label: string; color: string | null; count: number }>;
  nodes_missing_position?: Array<{ name: string }>;
}

export interface GeoMapProps {
  /** sm | md | lg height */
  format?: 'sm' | 'md' | 'lg';
  /** Show control bar (filters). Default true. */
  showControls?: boolean;
  className?: string;
  /** Poll interval ms */
  refreshMs?: number;
  /**
   * Show private/customer local network markers. Default false —
   * public map first; operator turns local ranges on explicitly.
   */
  defaultShowLocal?: boolean;
  /** Include private caller IPs. Default false. */
  defaultShowPrivateCallers?: boolean;
  /** Active sessions (operator↔node). Default false — opt-in. */
  defaultShowSessions?: boolean;
  /** Attack sources linked to nodes (self-defence). Default false. */
  defaultShowAttacks?: boolean;
  /** Optional kind filter passed to map_snapshot (e.g. ['node']). */
  kinds?: string[];
  /** Include API/MCP callers on map. Default true. */
  includeCallers?: boolean;
  /** Preset: defence map = nodes + attacks on. */
  preset?: 'default' | 'defence';
}

function numCoord(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < -90 || n > 90) {
    // could be lon passed as lat — still validate range per axis in caller
  }
  return n;
}

function validLatLon(lat: unknown, lon: unknown): { lat: number; lon: number } | null {
  const la = numCoord(lat);
  const lo = numCoord(lon);
  if (la == null || lo == null) return null;
  if (la < -90 || la > 90 || lo < -180 || lo > 180) return null;
  return { lat: la, lon: lo };
}

const KIND_COLORS: Record<string, string> = {
  node: '#0ea5e9',
  operator: '#10b981',
  device: '#8b5cf6',
  vehicle: '#f59e0b',
  asset: '#f97316',
  traccar_device: '#d946ef',
  caller: '#f87171',
  manual: '#9ca3af',
  network: '#6366f1',
};

function heightFor(format: 'sm' | 'md' | 'lg'): number {
  if (format === 'sm') return 220;
  if (format === 'md') return 360;
  return 480;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function makeDivIcon(
  color: string,
  opts: { blink?: boolean; approximate?: boolean; session?: boolean; label?: string } = {},
): L.DivIcon {
  const safeColor = /^#[0-9a-fA-F]{3,8}$/.test(color) || /^rgb/.test(color) ? color : '#94a3b8';
  const pulse = opts.blink
    ? `box-shadow:0 0 0 0 ${safeColor};animation:acm-geo-pulse 1.2s infinite;`
    : '';
  const title = escapeHtml((opts.label ?? '').replace(/<[^>]*>/g, ' ').slice(0, 120));
  // Approximate (no GPS): hollow ring. Session: diamond. Precise: solid fill.
  let body: string;
  if (opts.session) {
    body = `<div style="
      width:10px;height:10px;transform:rotate(45deg);
      background:${safeColor};border:1.5px solid rgba(255,255,255,.9);
      ${pulse}
    " title="${title}"></div>`;
  } else if (opts.approximate) {
    body = `<div style="
      width:14px;height:14px;border-radius:50%;
      background:transparent;border:2.5px dashed ${safeColor};
      box-shadow:0 0 0 1px rgba(0,0,0,.35);
      ${pulse}
    " title="${title}"></div>`;
  } else {
    body = `<div style="
      width:12px;height:12px;border-radius:50%;
      background:${safeColor};border:2px solid rgba(255,255,255,.85);
      ${pulse}
    " title="${title}"></div>`;
  }
  return L.divIcon({
    className: 'acm-geo-marker',
    html: body,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// Inject pulse keyframes once
let pulseCss = false;
function ensurePulseCss() {
  if (pulseCss || typeof document === 'undefined') return;
  pulseCss = true;
  const s = document.createElement('style');
  s.textContent = `
    @keyframes acm-geo-pulse {
      0% { box-shadow: 0 0 0 0 currentColor; transform: scale(1); }
      70% { box-shadow: 0 0 0 10px transparent; transform: scale(1.15); }
      100% { box-shadow: 0 0 0 0 transparent; transform: scale(1); }
    }
    .acm-geo-marker { background: transparent !important; border: none !important; }
    .leaflet-container { background: #0b1220; font: inherit; }
  `;
  document.head.appendChild(s);
}

// ── Component ────────────────────────────────────────────────────────────────

export function GeoMap({
  format = 'lg',
  showControls = true,
  className = '',
  refreshMs = 30_000,
  defaultShowLocal = false,
  defaultShowPrivateCallers = false,
  defaultShowSessions = false,
  defaultShowAttacks = false,
  kinds,
  includeCallers = true,
  preset = 'default',
}: GeoMapProps) {
  const { t } = useTranslation();
  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  const fittedRef = useRef(false);
  const syncedOnce = useRef(false);

  const isDefence = preset === 'defence';

  // Default: veřejná mapa (bez privátních rozsahů) — lokální sítě jen na výslovné zapnutí.
  const [showLocal, setShowLocal] = useState(defaultShowLocal);
  const [showPrivateCallers, setShowPrivateCallers] = useState(defaultShowPrivateCallers);
  const [showSessions, setShowSessions] = useState(defaultShowSessions);
  const [showKeys, setShowKeys] = useState(true);
  const [sessionWindow, setSessionWindow] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [sessionScope, setSessionScope] = useState<'cluster' | 'local' | 'node'>('cluster');
  const [sessionNodeId, setSessionNodeId] = useState('');
  const [showAttacks, setShowAttacks] = useState(defaultShowAttacks || isDefence);
  const [customerId, setCustomerId] = useState('');
  const [blinkCustomer, setBlinkCustomer] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const kindsKey = kinds?.slice().sort().join(',') ?? '';

  // Cluster node names for scope filter (from inventory / summary)
  const { data: topoNodes } = useQuery({
    queryKey: ['geo-map-cluster-nodes'],
    queryFn: async () => {
      const r = await api.mcp<{
        ok?: boolean;
        cluster?: Array<{ node_id?: string; name?: string; hostname?: string }>;
        nodes?: Array<{ node_id?: string; name?: string }>;
      }>('mesh.topology', {});
      const list: string[] = [];
      for (const n of r?.cluster ?? r?.nodes ?? []) {
        const id = n.node_id || n.name || n.hostname;
        if (id) list.push(String(id));
      }
      return [...new Set(list)].sort();
    },
    staleTime: 60_000,
    enabled: showSessions,
  });

  const { data, isLoading, isError, refetch, isFetching, error } = useQuery({
    queryKey: [
      'geo-map',
      showLocal,
      showPrivateCallers,
      showSessions,
      showKeys,
      sessionWindow,
      sessionScope,
      sessionNodeId,
      showAttacks,
      customerId,
      kindsKey,
      includeCallers,
      preset,
    ],
    queryFn: async () => {
      // Výhradně MCP tool geo.map_snapshot (žádný REST).
      const r = await api.mcp<MapSnapshot>('geo.map_snapshot', {
        include_local_networks: showLocal,
        include_private_callers: showPrivateCallers,
        include_sessions: showSessions,
        include_keys: showSessions && showKeys,
        session_window: sessionWindow,
        session_scope: sessionScope === 'node' && !sessionNodeId ? 'cluster' : sessionScope,
        ...(sessionScope === 'node' && sessionNodeId
          ? { session_node_id: sessionNodeId }
          : {}),
        include_attacks: showAttacks,
        include_missing_nodes: true,
        include_callers: isDefence ? false : includeCallers,
        ...(kinds?.length ? { kinds } : isDefence ? { kinds: ['node', 'operator'] } : {}),
        ...(customerId ? { customer_id: customerId } : {}),
      });
      if (!r || (r as { ok?: boolean }).ok === false) {
        throw new Error((r as { error?: string })?.error || 'map_snapshot failed');
      }
      return r;
    },
    refetchInterval: refreshMs,
    retry: 1,
  });

  // Once: ensure nodes exist in geo_positions (ip-geo / timezone)
  useEffect(() => {
    if (syncedOnce.current) return;
    syncedOnce.current = true;
    api.mcp('geo.node_sync', { geocode_limit: 3, force_local: false })
      .then(() => refetch())
      .catch(() => {});
  }, [refetch]);

  const syncNodes = async () => {
    setSyncing(true);
    try {
      await api.mcp('geo.node_sync', { geocode_limit: 5, force_local: true });
      await refetch();
    } catch { /* toast optional */ }
    setSyncing(false);
  };

  const center = useMemo(() => {
    const c = data?.default_center;
    const lat = numCoord(c?.lat) ?? 50.0755;
    const lon = numCoord(c?.lon) ?? 14.4378;
    const zoom = numCoord(c?.zoom) ?? 6;
    return { lat, lon, zoom: Math.min(18, Math.max(2, zoom)) };
  }, [data?.default_center]);

  const h = heightFor(format);

  // Init map once — always shows OSM tiles (even with zero markers)
  useEffect(() => {
    ensurePulseCss();
    const el = mapEl.current;
    if (!el) return;

    // Guard against double-init (React StrictMode / tab remount)
    if (mapRef.current) {
      try {
        mapRef.current.remove();
      } catch { /* ignore */ }
      mapRef.current = null;
      layerRef.current = null;
    }
    // Leaflet leaves classes on container — clean if remounting same DOM node
    el.innerHTML = '';

    let map: L.Map;
    try {
      map = L.map(el, {
        zoomControl: true,
        attributionControl: true,
      }).setView([center.lat, center.lon], center.zoom);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
        maxZoom: 19,
      }).addTo(map);

      layerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
    } catch (e) {
      console.error('[GeoMap] init failed', e);
      return;
    }

    // Fix size after mount (hidden tabs / widgets / 0-height first paint)
    const t1 = window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch { /* ignore */ }
    }, 80);
    const t2 = window.setTimeout(() => {
      try {
        map.invalidateSize();
      } catch { /* ignore */ }
    }, 400);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      try {
        map.remove();
      } catch { /* ignore */ }
      mapRef.current = null;
      layerRef.current = null;
      fittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invalidate size when height/format changes
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const t = window.setTimeout(() => {
      try {
        m.invalidateSize();
      } catch { /* ignore */ }
    }, 50);
    return () => window.clearTimeout(t);
  }, [h, format]);

  type MarkerSpec = {
    key: string;
    lat: number;
    lon: number;
    color: string;
    blink: boolean;
    approximate: boolean;
    session: boolean;
    popup: string;
    customer_id?: string | null;
  };

  type LinkSpec = {
    key: string;
    from: [number, number];
    to: [number, number];
    color?: string;
  };

  const { markers, links } = useMemo(() => {
    const list: MarkerSpec[] = [];
    const linkList: LinkSpec[] = [];

    for (const p of data?.positions ?? []) {
      const ll = validLatLon(p.lat, p.lon);
      if (!ll) continue;
      const approx = !!p.missing_precise_position || p.source === 'timezone-approx';
      const place = p.metadata?.place ? String(p.metadata.place) : '';
      const tz = p.metadata?.timezone ? String(p.metadata.timezone) : '';
      list.push({
        key: `p-${p.kind}-${p.entity_id}`,
        lat: ll.lat,
        lon: ll.lon,
        color: KIND_COLORS[p.kind] ?? KIND_COLORS.manual!,
        blink: false,
        approximate: approx,
        session: false,
        popup: [
          `<b>${escapeHtml(p.label || p.entity_id)}</b>`,
          `${escapeHtml(p.kind)} · ${escapeHtml(p.source)}`,
          approx
            ? `<i>${escapeHtml(t('geo.noPreciseLocation', 'bez přesné polohy'))}${tz ? ' · ' + escapeHtml(tz) : ''}${
              place ? ' (~' + escapeHtml(place) + ')' : ''
            }</i>`
            : '',
          `${Number(ll.lat).toFixed(4)}, ${Number(ll.lon).toFixed(4)}`,
        ].filter(Boolean).join('<br/>'),
      });
    }

    const posCallerKeys = new Set(
      (data?.positions ?? []).filter((p) => p.kind === 'caller').map((p) => p.entity_id),
    );
    for (const c of data?.callers ?? []) {
      const ll = validLatLon(c.lat, c.lon);
      if (!ll) continue;
      if (posCallerKeys.has(c.ip)) continue;
      const cust = c.customer_id ?? null;
      const blink = blinkCustomer && !!customerId && cust === customerId;
      list.push({
        key: `c-${c.ip}`,
        lat: ll.lat,
        lon: ll.lon,
        color: c.color || (c.is_private ? '#a78bfa' : KIND_COLORS.caller!),
        blink,
        approximate: false,
        session: false,
        customer_id: cust,
        popup: `<b>${escapeHtml(c.assigned_label || c.customer_label || c.ip)}</b><br/>${
          c.is_private ? 'private' : 'public'
        }${c.city ? ' · ' + escapeHtml(c.city) : ''}${
          c.country_code ? ' · ' + escapeHtml(c.country_code) : ''
        }${c.customer_label ? '<br/>' + escapeHtml(c.customer_label) : ''}`,
      });
    }

    for (const n of data?.network_markers ?? []) {
      if (!showLocal && n.is_private) continue;
      const ll = validLatLon(n.lat, n.lon);
      if (!ll) continue;
      const blink = blinkCustomer && !!customerId && n.customer_id === customerId;
      list.push({
        key: `n-${n.id}`,
        lat: ll.lat,
        lon: ll.lon,
        color: n.color || KIND_COLORS.network!,
        blink,
        approximate: false,
        session: false,
        customer_id: n.customer_id,
        popup: `<b>${escapeHtml(n.label)}</b><br/>${escapeHtml(n.cidr)} · ${escapeHtml(n.kind)}${
          n.is_private ? ' · private' : ' · public'
        }${n.customer_label ? '<br/>' + escapeHtml(n.customer_label) : ''}`,
      });
    }

    if (showSessions) {
      for (const s of data?.sessions ?? []) {
        const pin = validLatLon(s.lat, s.lon);
        const node = validLatLon(s.node_lat, s.node_lon);
        if (pin && node) {
          linkList.push({
            key: `link-${s.session_id}`,
            from: [pin.lat, pin.lon],
            to: [node.lat, node.lon],
            color: '#fbbf24',
          });
        }
        // Bez vlastní geolokace (privátní IP, cizí uzel) se session kreslí na
        // pozici obsluhujícího uzlu jako přibližná. Dřív se zahodila úplně —
        // mapa pak ukazovala jen lokální operátory s browser geo (2026-09-02).
        // Deterministický rozptyl (~±2 km z hash session_id), aby se body na
        // stejném uzlu neslily do jednoho a popupy zůstaly klikatelné.
        let pos = pin;
        if (!pos && node) {
          let h = 0;
          for (const ch of String(s.session_id)) h = (h * 31 + ch.charCodeAt(0)) | 0;
          const hu = Math.abs(h);
          pos = {
            lat: node.lat + ((hu >> 8) % 100) / 100 * 0.04 - 0.02,
            lon: node.lon + (hu % 100) / 100 * 0.06 - 0.03,
          };
        }
        if (pos) {
          list.push({
            key: `s-${s.session_id}`,
            lat: pos.lat,
            lon: pos.lon,
            color: '#fbbf24',
            blink: true,
            approximate: !pin,
            session: true,
            popup: [
              `<b>${escapeHtml(s.username || s.entity_id)}</b>`,
              `session → <b>${escapeHtml(s.node_id)}</b>`,
              s.ip_address ? `IP ${escapeHtml(s.ip_address)}` : '',
              s.auth_type ? escapeHtml(s.auth_type) : '',
              s.last_active ? escapeHtml(String(s.last_active).slice(0, 19)) : '',
              !pin ? t('geo.geoPoziceUzluPriblizna', 'geo: pozice uzlu (přibližná)') : s.geo_source ? `geo: ${escapeHtml(s.geo_source)}` : '',
            ].filter(Boolean).join('<br/>'),
          });
        }
      }
      if (showKeys) {
        for (const k of data?.keys ?? []) {
          const pin = validLatLon(k.lat, k.lon);
          if (!pin) continue;
          list.push({
            key: `k-${k.id}`,
            lat: pin.lat,
            lon: pin.lon,
            color: '#a78bfa',
            blink: false,
            approximate: false,
            session: true,
            popup: [
              `<b>🔑 ${escapeHtml(k.label)}</b>`,
              k.key_preview ? escapeHtml(k.key_preview) : '',
              k.username ? escapeHtml(k.username) : '',
              `uses: ${k.use_count}`,
              k.ip_address ? `IP ${escapeHtml(k.ip_address)}` : '',
              k.last_used ? escapeHtml(String(k.last_used).slice(0, 19)) : '',
            ].filter(Boolean).join('<br/>'),
          });
        }
      }
    }

    if (showAttacks) {
      for (const at of data?.attacks ?? []) {
        const ll = validLatLon(at.lat, at.lon);
        if (!ll) continue;
        list.push({
          key: `atk-${at.ip}`,
          lat: ll.lat,
          lon: ll.lon,
          color: at.blacklisted ? '#ef4444' : '#f97316',
          blink: true,
          approximate: false,
          session: false,
          popup: [
            `<b>⚔ ${escapeHtml(at.ip)}</b>`,
            at.blacklisted ? '<span style="color:#f87171">blacklist</span>' : 'security log',
            `events: ${at.event_count}`,
            at.kinds?.length ? escapeHtml(at.kinds.join(', ')) : '',
            at.city || at.country_code
              ? escapeHtml([at.city, at.country_code].filter(Boolean).join(', '))
              : '',
            at.target_nodes?.length
              ? '→ nodes: ' + escapeHtml(at.target_nodes.join(', '))
              : '',
          ].filter(Boolean).join('<br/>'),
        });
        for (const ln of at.links ?? []) {
          const nll = validLatLon(ln.node_lat, ln.node_lon);
          if (!nll) continue;
          linkList.push({
            key: `atk-link-${at.ip}-${ln.node_id}`,
            from: [ll.lat, ll.lon],
            to: [nll.lat, nll.lon],
            color: '#ef4444',
          });
        }
      }
    }

    if (customerId && blinkCustomer) {
      for (const m of list) {
        if (m.customer_id === customerId) m.blink = true;
      }
    }

    return { markers: list, links: linkList };
  }, [data, customerId, blinkCustomer, showLocal, showSessions, showKeys, showAttacks]);

  // Draw markers + session links (never throw into React)
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;

    try {
      layer.clearLayers();
      const bounds: L.LatLngTuple[] = [];

      for (const ln of links) {
        try {
          L.polyline([ln.from, ln.to], {
            color: ln.color || '#fbbf24',
            weight: 2,
            opacity: 0.65,
            dashArray: '6 4',
          }).addTo(layer);
          bounds.push(ln.from, ln.to);
        } catch { /* ignore */ }
      }

      for (const m of markers) {
        try {
          const marker = L.marker([m.lat, m.lon], {
            icon: makeDivIcon(m.color, {
              blink: m.blink,
              approximate: m.approximate,
              session: m.session,
              label: m.popup,
            }),
          });
          marker.bindPopup(m.popup);
          marker.addTo(layer);
          bounds.push([m.lat, m.lon]);
        } catch (e) {
          console.warn('[GeoMap] marker skip', m.key, e);
        }
      }

      try {
        map.invalidateSize();
      } catch { /* ignore */ }

      if (bounds.length > 0) {
        try {
          const b = L.latLngBounds(bounds);
          if (b.isValid()) {
            map.fitBounds(b, { padding: [28, 28], maxZoom: 12, animate: false });
            fittedRef.current = true;
          }
        } catch { /* ignore */ }
      } else if (!fittedRef.current) {
        try {
          map.setView([center.lat, center.lon], center.zoom, { animate: false });
        } catch { /* ignore */ }
      }
    } catch (e) {
      console.error('[GeoMap] draw failed', e);
    }
  }, [markers, links, center.lat, center.lon, center.zoom]);

  const customers = data?.customers ?? [];
  const stats = data?.summary;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {showControls && (
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='secondary'
            size='sm'
            icon={<RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />}
            onClick={() => refetch()}
          >
            {t('geo.refresh')}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            icon={<Server size={12} />}
            loading={syncing}
            onClick={() => void syncNodes()}
            title={t('geo.syncNodesHint', 'Doplnit nody (IP geo / timezone)')}
          >
            {t('geo.syncNodes', 'Sync nody')}
          </Button>
          <Button
            variant={showLocal ? 'primary' : 'secondary'}
            size='sm'
            icon={showLocal ? <Eye size={12} /> : <EyeOff size={12} />}
            onClick={() => setShowLocal((v) => !v)}
            title={t('geo.toggleLocalNetworks')}
          >
            {t('geo.localNetworks')}
          </Button>
          <Button
            variant={showPrivateCallers ? 'primary' : 'secondary'}
            size='sm'
            onClick={() => setShowPrivateCallers((v) => !v)}
            title={t('geo.togglePrivateCallers')}
          >
            {t('geo.privateCallers')}
          </Button>
          <Button
            variant={showSessions ? 'primary' : 'secondary'}
            size='sm'
            icon={<Link2 size={12} />}
            onClick={() => setShowSessions((v) => !v)}
            title={t(
              'geo.toggleSessions',
              'Sessions + klíče (ACL: já / tenant admin / node admin)',
            )}
          >
            {t('geo.sessions', 'Sessions')}
          </Button>
          {showSessions && (
            <>
              <Select
                size='sm'
                className='min-w-[5.5rem]'
                value={sessionWindow}
                onChange={(e) =>
                  setSessionWindow(e.target.value as '1h' | '24h' | '7d' | '30d')}
                options={[
                  { value: '1h', label: t('geo.window1h', '1 h') },
                  { value: '24h', label: t('geo.window24h', '24 h') },
                  { value: '7d', label: t('geo.window7d', '7 dní') },
                  { value: '30d', label: t('geo.window30d', '30 dní') },
                ]}
              />
              <Select
                size='sm'
                className='min-w-[7rem]'
                value={sessionScope}
                onChange={(e) => {
                  const v = e.target.value as 'cluster' | 'local' | 'node';
                  setSessionScope(v);
                  if (v !== 'node') setSessionNodeId('');
                }}
                options={[
                  { value: 'cluster', label: t('geo.scopeCluster', 'Cluster') },
                  { value: 'local', label: t('geo.scopeLocal', 'Tento node') },
                  { value: 'node', label: t('geo.scopeNode', 'Jeden node…') },
                ]}
              />
              {sessionScope === 'node' && (
                <Select
                  size='sm'
                  className='min-w-[8rem]'
                  value={sessionNodeId}
                  onChange={(e) => setSessionNodeId(e.target.value)}
                  options={[
                    { value: '', label: t('geo.pickNode', '— node —') },
                    ...(topoNodes ?? []).map((n) => ({ value: n, label: n })),
                    // also nodes seen in activity summary
                    ...((data?.activity_acl as { summary?: { source_nodes?: string[] } })
                      ?.summary?.source_nodes ?? [])
                      .filter((n) => !(topoNodes ?? []).includes(n))
                      .map((n) => ({ value: n, label: n })),
                  ]}
                />
              )}
              <Button
                variant={showKeys ? 'primary' : 'secondary'}
                size='sm'
                onClick={() => setShowKeys((v) => !v)}
                title={t('geo.toggleKeys', 'API klíče s last_used geo')}
              >
                {t('geo.keys', 'Klíče')}
              </Button>
            </>
          )}
          <Button
            variant={showAttacks ? 'primary' : 'secondary'}
            size='sm'
            onClick={() => setShowAttacks((v) => !v)}
            title={t('geo.toggleAttacks', 'Zdroje útoků → nody')}
          >
            {t('geo.attacks', 'Útoky')}
          </Button>
          <div className='flex items-center gap-1'>
            <Building2 size={12} className='text-gray-500' />
            <Select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className='text-xs min-w-[9rem]'
            >
              <option value=''>{t('geo.allCustomers')}</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label} ({c.count})
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant={blinkCustomer ? 'primary' : 'secondary'}
            size='sm'
            icon={<Zap size={12} />}
            onClick={() => setBlinkCustomer((v) => !v)}
            disabled={!customerId}
            title={t('geo.blinkCustomer')}
          >
            {t('geo.blink')}
          </Button>
          <div className='ml-auto flex flex-wrap gap-1 text-[10px] text-gray-500'>
            {stats && (
              <>
                <Badge variant='default'>
                  <MapPin size={10} className='inline mr-0.5' />
                  {stats.positions}
                </Badge>
                <Badge variant='info'>
                  nodes {stats.by_kind?.node ?? stats.inventory_nodes ?? '—'}
                </Badge>
                {(stats.nodes_approximate ?? 0) > 0 && (
                  <Badge variant='warning'>
                    ~{stats.nodes_approximate}
                  </Badge>
                )}
                <Badge variant='info'>
                  IP {stats.callers_with_coords ?? stats.callers_mapped ?? stats.callers ?? 0}
                </Badge>
                {showSessions && (
                  <>
                    <Badge variant='default'>
                      sess {stats.sessions ?? 0} · {sessionWindow}
                    </Badge>
                    {showKeys && (
                      <Badge variant='purple'>keys {stats.keys ?? data?.keys?.length ?? 0}</Badge>
                    )}
                    {data?.activity_acl?.mode && (
                      <Badge variant='info'>ACL {data.activity_acl.mode}</Badge>
                    )}
                    <Badge variant='default'>
                      {sessionScope}
                      {(data?.activity_acl?.summary?.sessions_cluster ?? 0) > 0
                        ? ` · +${data?.activity_acl?.summary?.sessions_cluster} peer`
                        : ''}
                    </Badge>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div
        className='relative w-full rounded-lg overflow-hidden border border-gray-800'
        style={{ height: h }}
      >
        <div ref={mapEl} className='absolute inset-0 z-0' />
        {isLoading && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-black/30 text-xs text-gray-300 pointer-events-none'>
            {t('common.loading')}
          </div>
        )}
        {isError && (
          <div className='absolute top-2 left-2 z-10 text-xs text-red-400 bg-black/70 px-2 py-1 rounded max-w-[90%]'>
            {t('geo.mapLoadError')}
            {error instanceof Error && error.message
              ? `: ${error.message.slice(0, 120)}`
              : ''}
          </div>
        )}
        {!isLoading && markers.length === 0 && (
          <div className='absolute bottom-2 left-2 z-10 text-[10px] text-gray-300 bg-black/60 px-2 py-1 rounded pointer-events-none'>
            {t('geo.mapEmptyHint')}
          </div>
        )}
      </div>

      {showControls && (
        <div className='flex flex-wrap gap-3 text-[10px] text-gray-500'>
          {Object.entries(KIND_COLORS).map(([k, c]) => (
            <span key={k} className='inline-flex items-center gap-1'>
              <span className='w-2 h-2 rounded-full' style={{ background: c }} />
              {k}
            </span>
          ))}
          <span className='inline-flex items-center gap-1'>
            <span
              className='w-2.5 h-2.5 rounded-full border-2 border-dashed border-sky-400'
              style={{ background: 'transparent' }}
            />
            {t('geo.legendApprox', 'bez GPS (timezone)')}
          </span>
          <span className='inline-flex items-center gap-1'>
            <span className='w-2 h-2 bg-amber-400 rotate-45' />
            {t('geo.legendSession', 'session')}
          </span>
        </div>
      )}
    </div>
  );
}

export default GeoMap;
